import React, { useEffect, useMemo, useState } from 'react'
import { CheckIcon, Loader2Icon } from 'lucide-react'
import {
  completeInboxTask,
  fetchInboxTasks,
  isTaskOverdue,
  sortTasksForQueue,
  updateInboxTask,
} from '../../api/inboxTasks'
import { assigneeDisplayName, canCompleteInboxTask } from '../../lib/inboxPermissions'
import type { InboxAssignee, InboxTask } from '../../types/inbox'

interface InboxTasksTabProps {
  currentUserId: string | null
  currentUserRole: string | null
  assignees: InboxAssignee[]
  onOpenSource?: (sourceType: string, sourceId: string) => void
}

export function InboxTasksTab({
  currentUserId,
  currentUserRole,
  assignees,
  onOpenSource,
}: InboxTasksTabProps) {
  const [tasks, setTasks] = useState<InboxTask[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'mine' | 'all' | 'overdue'>('mine')

  const assigneeNameById = useMemo(
    () => new Map(assignees.map((a) => [a.id, assigneeDisplayName(a)])),
    [assignees]
  )

  useEffect(() => {
    setLoading(true)
    fetchInboxTasks({ status: ['open', 'in_progress', 'blocked', 'snoozed'], limit: 500 })
      .then((rows) => setTasks(sortTasksForQueue(rows)))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    let rows = tasks
    if (filter === 'mine' && currentUserId) rows = rows.filter((t) => t.assignee_id === currentUserId)
    if (filter === 'overdue') rows = rows.filter((t) => isTaskOverdue(t))
    return sortTasksForQueue(rows)
  }, [tasks, filter, currentUserId])

  async function markDone(task: InboxTask) {
    if (!canCompleteInboxTask(currentUserRole, currentUserId, task.assignee_id)) return
    await completeInboxTask(task.id)
    setTasks((prev) => prev.filter((t) => t.id !== task.id))
  }

  async function startTask(task: InboxTask) {
    if (task.status === 'open') {
      const updated = await updateInboxTask(task.id, { status: 'in_progress' })
      setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-white flex items-center gap-2">
        {(['mine', 'all', 'overdue'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full ${
              filter === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {key === 'mine' ? 'My open tasks' : key === 'all' ? 'All open' : 'Overdue'}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-16">No tasks in this view.</p>
        ) : (
          <div className="divide-y divide-gray-200 bg-white">
            {filtered.map((task) => (
              <div key={task.id} className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50">
                <button
                  type="button"
                  disabled={!canCompleteInboxTask(currentUserRole, currentUserId, task.assignee_id)}
                  onClick={() => markDone(task)}
                  className="mt-1 p-1.5 rounded-lg border border-gray-200 hover:bg-green-50 disabled:opacity-40"
                >
                  <CheckIcon className="w-4 h-4 text-green-600" />
                </button>
                <button type="button" className="flex-1 min-w-0 text-left" onClick={() => startTask(task)}>
                  <p className={`text-sm font-semibold truncate ${isTaskOverdue(task) ? 'text-red-600' : 'text-gray-900'}`}>
                    {task.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {assigneeNameById.get(task.assignee_id) || 'Assignee'} · {task.status.replace('_', ' ')}
                    {task.due_at ? ` · Due ${new Date(task.due_at).toLocaleString()}` : ''}
                  </p>
                  {task.action_type && (
                    <span className="inline-block mt-1 text-[10px] uppercase tracking-wide text-gray-400">
                      {task.action_type.replace(/_/g, ' ')}
                    </span>
                  )}
                </button>
                {onOpenSource && (
                  <button
                    type="button"
                    onClick={() => onOpenSource(task.source_type, task.source_id)}
                    className="text-xs text-blue-600 hover:underline flex-shrink-0"
                  >
                    Open source
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
